import { Button, Flex, Text, Radio } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { closeModal, openModal } from '@mantine/modals';
import classes from './useVacancyChangeActivityModal.module.scss';
import { IconCalendar } from '@tabler/icons-react';
import { MODAL_SIZE_MEDIUM } from '@shared/ui/modal/constants';
import { useForm } from '@mantine/form';
import {
  useUpdateVacancyStatus,
  type UpdateVacancyStatusRequest,
} from '@entities/vacancy';
import {
  UseVacancyChangeActivitySchema,
  type UseVacancyChangeActivityFormValues,
} from './types';
import { zodResolver } from 'mantine-form-zod-resolver';
import { showNotification } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKey } from '@shared/config';

const MODAL_ID = 'change-activity-vacancy';

type UseVacancyChangeActivityModalComponentProps = {
  id: string;
  status: string;
};

const UseVacancyChangeActivityModalComponent = ({
  id,
  status,
}: UseVacancyChangeActivityModalComponentProps) => {
  const queryClient = useQueryClient();

  const { mutate: updateVacancyStatus, isPending } = useUpdateVacancyStatus(id);

  const handleClose = () => {
    closeModal(MODAL_ID);
  };

  const form = useForm<UseVacancyChangeActivityFormValues>({
    initialValues: {
      activityType: 'always',
      activeFrom: null,
      activeTo: null,
    },
    validate: zodResolver(UseVacancyChangeActivitySchema),
  });

  const handleSubmit = (values: UseVacancyChangeActivityFormValues) => {
    let payload: UpdateVacancyStatusRequest = {
      status,
      activeFrom: values.activeFrom?.toISOString() ?? '',
    };

    if (values.activeTo) {
      payload = {
        ...payload,
        activeTo: values.activeTo?.toISOString() ?? null,
      };
    }

    updateVacancyStatus(payload, {
      onSuccess: () => {
        queryClient
          .invalidateQueries({
            queryKey: [QueryKey.GET_VACANCY, Number(id)],
          })
          .then(() => {
            showNotification({
              variant: 'success',
              message:
                'Начало работы (или период работы для временных вакансий) успешно обновлен',
            });

            closeModal(MODAL_ID);
          });
      },
      onError: error => {
        showNotification({
          error,
          variant: 'error',
          message:
            error.response?.data?.message ||
            'Не удалось обновить активности вакансии',
        });

        closeModal(MODAL_ID);
      },
    });
  };

  return (
    <Flex w='100%' flex={1}>
      <form className={classes.form} onSubmit={form.onSubmit(handleSubmit)}>
        <Flex direction='column' gap={16} pb={16}>
          <Text c='tertiary.7' size='sm'>
            Установите период активности вакансии
          </Text>

          <Radio.Group
            key={form.key('activityType')}
            {...form.getInputProps('activityType')}
          >
            <Flex
              className={classes.row}
              direction={{ base: 'column', sm: 'row' }}
            >
              <Radio
                value='always'
                label='Всегда активна'
                w={{ base: '100%', sm: '50%' }}
              />
              <Radio
                value='period'
                label='На период'
                w={{ base: '100%', sm: '50%' }}
              />
            </Flex>
          </Radio.Group>

          <Flex
            className={classes.row}
            direction={{ base: 'column', sm: 'row' }}
          >
            <DateInput
              key={form.key('activeFrom')}
              {...form.getInputProps('activeFrom')}
              placeholder='с'
              w={{ base: '100%', sm: '50%' }}
              leftSection={
                <IconCalendar color='var(--mantine-color-primary-4)' />
              }
              clearable
            />
            <DateInput
              key={form.key('activeTo')}
              {...form.getInputProps('activeTo')}
              placeholder='до'
              w={{ base: '100%', sm: '50%' }}
              leftSection={
                <IconCalendar color='var(--mantine-color-primary-4)' />
              }
              disabled={form.values.activityType === 'always'}
              clearable
            />
          </Flex>
        </Flex>

        <Flex gap={8} mt={12}>
          <Button variant='gray' onClick={handleClose}>
            Отмена
          </Button>
          <Button type='submit' loading={isPending} disabled={isPending}>
            Сохранить
          </Button>
        </Flex>
      </form>
    </Flex>
  );
};

export const useVacancyChangeActivityModal = () => {
  const openChangeActivityVacancy = (id: string, status: string) =>
    openModal({
      modalId: MODAL_ID,
      title: 'Начало работы (или период работы для временных вакансий)',
      size: MODAL_SIZE_MEDIUM,
      children: (
        <UseVacancyChangeActivityModalComponent id={id} status={status} />
      ),
    });

  return { openChangeActivityVacancy };
};
